'use client';

import { useAtomValue, useSetAtom } from 'jotai';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { localeAtom } from '@core/Atoms/Locale/Locale.atom';
import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import { currentCheckoutVehicleAtom } from '@core/Atoms/Vehicle/CurrentCheckoutVehicle.atom';
import { currentVehicleAtom } from '@core/Atoms/Vehicle/CurrentVehicle.atom';
import { currentVehicleIdAtom } from '@core/Atoms/Vehicle/CurrentVehicleId.atom';
import type { Tenant } from '@core/Entities/App/Tenants.entity';
import Button from '@gds/Button/Button';
import { Typography } from '@gds/Typography/Typography';
import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';
import { generateCheckoutStartTrackingData } from '@tracking/LeadCTA.tracking';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';

import { calculationAtom } from '../../Atoms/Calculation.atom';
import { hasClickedApplyFinanceAtom } from '../../Atoms/HasClickedApplyFinance.atom';
import { useNavigateToCheckout } from '../../Hooks/useNavigateToCheckout';
import { isLeasingFinancing } from '../../Utils/isFinanceCaptive';
import type { CalculatorMessages } from './Calculator.messages';
import { isNb, isPfs, isRciDe, isVwfs, mapperFinanceProducts } from './Helpers/FinanceMap';
import { SummaryUK } from './Summary/SummaryUK';

import styles from './Calculator.module.css';

export type CalculatorProps = {
  tenant: Tenant | undefined;
  strings: CalculatorMessages;
};

export const CalculatorSummary = ({ strings, tenant }: CalculatorProps) => {
  const calculation = useAtomValue(calculationAtom)!;
  const locale = useAtomValue(localeAtom);
  const flags = useAtomValue(flagsAtom);
  const pathname = usePathname();

  const setClickedApplyFinance = useSetAtom(hasClickedApplyFinanceAtom);
  const canShowUniversalCalc = flags.showUniversalCalculator;
  const showFinanceCalcCta = canShowUniversalCalc && !pathname.includes('/finance-deal');

  const { handleNavigateToCheckoutClick } = useNavigateToCheckout({ locale: 'uk' });
  const vehicle = useAtomValue(currentVehicleAtom)!;
  const isEcomNotVsp = vehicle.ecom?.isEcom && vehicle?.spec.d2cModel !== 'vsp';
  const isVsp = vehicle.spec.d2cModel === 'vsp';
  const financeRate = vehicle.finance?.rate;
  const showLenderNote = financeRate !== undefined;
  const userId = useAtomValue(userIdAtom);
  const formState = useAtomValue(formStateAtom);
  const listingId = useAtomValue(currentVehicleIdAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const setCurrentvehicle = useSetAtom(currentCheckoutVehicleAtom);
  const currentProductId = calculation?.selectedProduct;
  const product = calculation?.products?.[currentProductId as any];
  const isUkCheckoutVehicle = isEcomNotVsp || isVsp;

  const captiveId = calculation?.captiveId;
  const isCurrentVwfs = isVwfs(captiveId);
  const hasLeasingFinancing = isLeasingFinancing(calculation);
  const hasVwfsLeasing = hasLeasingFinancing && isCurrentVwfs;
  const isCurrentPorsche = isPfs(captiveId);
  const isCurrentMobilize = isRciDe(captiveId) || isNb(captiveId);
  const selectedProduct = calculation?.selectedProduct;
  const handleApplyClick = () => {
    setCurrentvehicle(vehicle);
    const {
      checkoutStart: { event, context },
    } = generateCheckoutStartTrackingData({
      searchParams,
      userId,
      formState,
      listingId,
      vehicle,
      ctaLocation: 'finance_calculator',
    });
    trackCustomEvent({ event, context });
    setClickedApplyFinance(true);
  };

  const getSummaryTitle = () => {
    if (hasVwfsLeasing) return mapperFinanceProducts.vwfsPrivateLeasing?.summaryTitle.uk;
    if (isCurrentPorsche && selectedProduct === 'privateLeasing')
      return strings.porscheSummaryTitle;
    if (isCurrentMobilize) return strings.mobilizeSummaryTitle;
    return mapperFinanceProducts[currentProductId]?.summaryTitle.uk;
  };

  return (
    <div
      className={`${
        canShowUniversalCalc ? styles.universalSummaryContainer : styles.summaryContainer
      }`}
    >
      <div className={styles.summarytitle}>
        <Typography variant="h5">{getSummaryTitle()}</Typography>

        <Typography variant="body4" Component="p">
          {mapperFinanceProducts[currentProductId]?.description.uk}
        </Typography>
      </div>

      {product && (
        <SummaryUK
          calculation={calculation}
          product={product}
          locale={'uk'}
          summaryStrings={strings}
        />
      )}

      <footer
        className={`${canShowUniversalCalc ? styles.universalCtasContainer : styles.ctasContainer}`}
      >
        {showLenderNote && (
          <Typography Component="p" variant="body2" className={styles.lenderNote}>
            {strings.lenderNote}
          </Typography>
        )}

        {isUkCheckoutVehicle ? (
          <Button
            Component={Link}
            dataTestId="apply_for_finance"
            variant="outlined"
            fullWidth
            href={`/checkout/${vehicle.heycarId}/about-you`}
            onClick={handleApplyClick}
            color={tenant === 'rac' ? 'primary' : 'secondary'}
          >
            {locale === 'uk' ? strings.submitToDealer : strings.startUniversalJourney}
          </Button>
        ) : (
          <Button
            variant="outlined"
            fullWidth
            type="submit"
            onClick={handleNavigateToCheckoutClick}
            color={tenant === 'rac' ? 'primary' : 'secondary'}
          >
            {locale === 'uk' ? strings.submitToDealer : strings.startUniversalJourney}
          </Button>
        )}
        {showFinanceCalcCta && (
          <Button
            className={styles.universalButton}
            variant="outlined"
            fullWidth={false}
            type="submit"
            onClick={handleNavigateToCheckoutClick}
            color={tenant === 'rac' ? 'primary' : 'secondary'}
          >
            {strings.startUniversalJourney}{' '}
          </Button>
        )}
      </footer>
    </div>
  );
};

CalculatorSummary.displayName = 'CalculatorSummary';
